import React, { useEffect, useImperativeHandle, useState } from 'react';

import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

import DetailFormHeader from 'components/condition-list/DetailFormHeader';
import FormSection from 'components/condition-list/FormSection';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import TextArea from 'components/form/TextArea';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { MasterTypeResult, getPartnerType } from 'stores/MasterTypeStoreAdapter';
import { createPartner } from 'stores/PartnerStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type PartnerCreateFormHandle = {
  submit: () => void;
};

type PartnerCreateFormProps = {
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

const PartnerCreateForm = React.forwardRef<PartnerCreateFormHandle, PartnerCreateFormProps>(
  ({ onCreate, onCancel, setProgress, hideHeader = false }, ref) => {
    const [partnerName, setPartnerName] = useState('');
    const [partnerNameKana, setPartnerNameKana] = useState('');
    const [partnerTypeId, setPartnerTypeId] = useState('');
    const [corporateNumber, setCorporateNumber] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [address, setAddress] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [mailAddress, setMailAddress] = useState('');
    const [contactName, setContactName] = useState('');
    const [contractStartDate, setContractStartDate] = useState('');
    const [remarks, setRemarks] = useState('');

    const [partnerTypes, setPartnerTypes] = useState<MasterTypeResult[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      getPartnerType()
        .then(setPartnerTypes)
        .catch(() => errorDefault('パートナー種別の取得に失敗しました'));
    }, []);

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      // 必須 + maxLength
      if (!partnerName) newErrors.partnerName = MSG_REQUIRED('パートナー名');
      else if (partnerName.length > 300)
        newErrors.partnerName = MSG_MAX_LENGTH('パートナー名', 300);

      if (!partnerNameKana) newErrors.partnerNameKana = MSG_REQUIRED('パートナー名（カナ）');
      else if (partnerNameKana.length > 300)
        newErrors.partnerNameKana = MSG_MAX_LENGTH('パートナー名（カナ）', 300);

      if (!partnerTypeId) newErrors.partnerTypeId = MSG_REQUIRED('パートナー種別');
      else if (partnerTypeId.length > 300)
        newErrors.partnerTypeId = MSG_MAX_LENGTH('パートナー種別', 300);

      // 契約開始日: 必須 + format
      if (!contractStartDate) newErrors.contractStartDate = MSG_REQUIRED('契約開始日');
      else if (!DATETIME_REGEX.test(contractStartDate))
        newErrors.contractStartDate = MSG_INVALID_DATETIME_FORMAT('契約開始日');

      // 任意項目の maxLength
      if (corporateNumber && corporateNumber.length > 20)
        newErrors.corporateNumber = MSG_MAX_LENGTH('法人番号', 20);

      if (invoiceNumber && invoiceNumber.length > 20)
        newErrors.invoiceNumber = MSG_MAX_LENGTH('インボイス番号', 20);

      if (address && address.length > 500) newErrors.address = MSG_MAX_LENGTH('住所', 500);

      if (phoneNumber && phoneNumber.length > 20)
        newErrors.phoneNumber = MSG_MAX_LENGTH('電話番号', 20);

      if (mailAddress && mailAddress.length > 300)
        newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);

      if (contactName && contactName.length > 300)
        newErrors.contactName = MSG_MAX_LENGTH('担当者名', 300);

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createPartner({
        partnerName,
        partnerNameKana,
        partnerTypeId,
        ...(corporateNumber && { corporateNumber }),
        ...(invoiceNumber && { invoiceNumber }),
        ...(address && { address }),
        ...(phoneNumber && { phoneNumber }),
        ...(mailAddress && { mailAddress }),
        ...(contactName && { contactName }),
        contractStartDate,
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('パートナーを作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('パートナーの作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader && (
          <DetailFormHeader
            title="パートナー登録"
            subtitle="新しいパートナーを登録します"
            onBack={onCancel}
          />
        )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          <FormSection title="基本情報">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="パートナー名"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.partnerName}
                  helperText={errors.partnerName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="パートナー名（カナ）"
                  value={partnerNameKana}
                  onChange={(v) => setPartnerNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.partnerNameKana}
                  helperText={errors.partnerNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControl required error={!!errors.partnerTypeId}>
                  <FormLabel sx={{ mb: 0.5 }}>パートナー種別</FormLabel>
                  <RadioGroup
                    row
                    value={partnerTypeId}
                    onChange={(e) => setPartnerTypeId(e.target.value)}
                  >
                    {partnerTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.partnerTypeId && <FormHelperText>{errors.partnerTypeId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="契約期間">
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={contractStartDate}
                  onChange={setContractStartDate}
                  label="契約開始日"
                  required
                  error={!!errors.contractStartDate}
                  helperText={errors.contractStartDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value=""
                  onChange={() => {}}
                  label="契約終了日"
                  disabled
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="法人情報">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="法人番号"
                  value={corporateNumber}
                  onChange={(e) => setCorporateNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.corporateNumber}
                  helperText={errors.corporateNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="インボイス番号"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="住所"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          <FormSection title="連絡先">
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="担当者名"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="電話番号"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メールアドレス"
                  value={mailAddress}
                  onChange={(e) => setMailAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.mailAddress}
                  helperText={errors.mailAddress}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

PartnerCreateForm.displayName = 'PartnerCreateForm';

export default PartnerCreateForm;
